import * as React from 'react';
import {
    DefaultButton,
    MessageBar,
    MessageBarType,
    Panel,
    PanelType,
    Spinner,
    SpinnerSize,
    Stack,
    Text,
} from '@fluentui/react';
import { ApiError, ApiHelper } from '../../../ApiHelper';
import type { Service } from '../../../interfaces/Service';
import type {
    AgentTaskPullRequestDetail,
    RepairAgentTaskPullRequestResult,
} from '../types';
import {
    buildAgencySessionUrl,
    buildAdoPullRequestUrl,
    buildWorkItemJobUrl,
    formatDate,
    getJobStatusLabel,
    getPullRequestRepairSuccessMessage,
    getPullRequestStatusWithHealthLabel,
} from '../helpers';
import { canResolvePullRequest } from '../details/PullRequestSummaryList';
import { PullRequestReviewerEmailButton } from '../PullRequestReviewerEmailButton';
import { RepairActionBadges } from '../repairActionPresentation';
import { PullRequestRepairProgress } from '../PullRequestRepairProgress';

export function PullRequestWorkItemHistory(props: {
    title: string;
    workItems: AgentTaskPullRequestDetail['workItems'];
    serviceName: string;
    isRepairHistory?: boolean;
}) {
    const chronologicalWorkItems = props.isRepairHistory
        ? [...props.workItems].sort((left, right) => left.workItemId - right.workItemId)
        : props.workItems;
    const displayedWorkItems = props.isRepairHistory
        ? [...chronologicalWorkItems].reverse()
        : chronologicalWorkItems;

    return (
        <section>
            <Text as="h3" variant="mediumPlus">{props.title}</Text>
            {props.workItems.length === 0 ? (
                <Text>None</Text>
            ) : (
                <Stack tokens={{ childrenGap: 8 }}>
                    {displayedWorkItems.map(workItem => {
                        const isExecuting = workItem.jobStatus === 2;
                        const isTerminal = [3, 4, 5, 6].includes(workItem.jobStatus);
                        const timestamp = isExecuting
                            ? workItem.agentStartDate
                            : isTerminal
                                ? workItem.agentEndDate ?? workItem.agentStartDate
                                : null;
                        const timestampLabel = isExecuting || (isTerminal && !workItem.agentEndDate)
                            ? 'Started'
                            : 'Finished';
                        const repairSessionUrl = props.isRepairHistory
                            ? buildAgencySessionUrl(workItem.agencyJobId)
                            : undefined;
                        const repairAttemptLabel = props.isRepairHistory
                            ? workItem.attemptNumber
                                ? `Repair attempt ${workItem.attemptNumber}`
                                : 'Repair attempt unavailable'
                            : null;

                        return (
                            <div
                                key={workItem.workItemId}
                                style={{ border: '1px solid #e1dfdd', borderRadius: 6, padding: 10 }}
                            >
                                <div>
                                    {repairAttemptLabel ? (
                                        repairSessionUrl ? (
                                            <a
                                                href={repairSessionUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{ fontWeight: 600 }}
                                            >
                                                {repairAttemptLabel}
                                            </a>
                                        ) : (
                                            <strong>{repairAttemptLabel}</strong>
                                        )
                                    ) : (
                                        <a
                                            href={buildWorkItemJobUrl(
                                                props.serviceName,
                                                workItem.jobName,
                                                workItem.agencyJobId,
                                            )}
                                            target={workItem.agencyJobId ? '_blank' : undefined}
                                            rel={workItem.agencyJobId ? 'noopener noreferrer' : undefined}
                                            style={{ fontWeight: 600 }}
                                        >
                                            {workItem.jobName}
                                        </a>
                                    )}
                                    {' • '}
                                    {getJobStatusLabel(workItem.jobStatus)}
                                </div>
                                {repairAttemptLabel && <div>{workItem.jobName}</div>}
                                {repairAttemptLabel && (
                                    <div style={{ marginTop: 6 }}>
                                        <RepairActionBadges repair={workItem} />
                                    </div>
                                )}
                                {timestamp && (
                                    <div>{timestampLabel}: {formatDate(timestamp)}</div>
                                )}
                                {workItem.jobStatus === 4 && workItem.error && (
                                    <details style={{ marginTop: 8 }}>
                                        <summary style={{ cursor: 'pointer' }}>Show failure details</summary>
                                        <div style={{ marginTop: 8, whiteSpace: 'pre-wrap' }}>{workItem.error}</div>
                                    </details>
                                )}
                            </div>
                        );
                    })}
                </Stack>
            )}
        </section>
    );
}

export function PullRequestDetailPanel(props: {
    isOpen: boolean;
    serviceName: string;
    service: Service | undefined;
    taskListId: number;
    taskId?: number;
    prId?: number;
    onDismiss: () => void;
    onResolved?: () => void;
    isResolving?: boolean;
    isNotActionable?: boolean;
    onResolutionStateChange?: (
        prId: number,
        state: 'resolving' | 'idle' | 'notActionable',
    ) => void;
}) {
    const {
        isOpen,
        serviceName,
        service,
        taskListId,
        taskId,
        prId,
        onDismiss,
        onResolved,
        isResolving,
        isNotActionable,
        onResolutionStateChange,
    } = props;
    const [detail, setDetail] = React.useState<AgentTaskPullRequestDetail | null>(null);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [resolveLoading, setResolveLoading] = React.useState(false);
    const [resolveMessage, setResolveMessage] = React.useState<string | null>(null);
    const [localNotActionable, setLocalNotActionable] = React.useState(false);
    const effectiveResolving = isResolving ?? resolveLoading;
    const effectiveNotActionable = isNotActionable ?? localNotActionable;

    const loadDetail = React.useCallback(() => {
        if (!isOpen || taskId === undefined || prId === undefined) {
            setDetail(null);
            return;
        }

        setLoading(true);
        setError(null);
        ApiHelper<AgentTaskPullRequestDetail>(
            `/api/service/${serviceName}/agenttasklists/${taskListId}/tasks/${taskId}/pullrequests/${prId}`,
        ).then(result => {
            setDetail(result);
            setLoading(false);
        }).catch((err: Error) => {
            setError(err.message || 'Failed to load pull request details.');
            setDetail(null);
            setLoading(false);
        });
    }, [isOpen, prId, serviceName, taskId, taskListId]);

    React.useEffect(() => {
        setResolveMessage(null);
        setLocalNotActionable(false);
        loadDetail();
    }, [loadDetail]);

    async function resolvePullRequest() {
        if (!detail || effectiveResolving) {
            return;
        }

        setResolveLoading(true);
        onResolutionStateChange?.(detail.prId, 'resolving');
        setError(null);
        setResolveMessage(null);
        try {
            const result = await ApiHelper<RepairAgentTaskPullRequestResult>(
                `/api/service/${serviceName}/agenttasklists/${taskListId}/tasks/${detail.taskId}/pullrequests/${detail.prId}/resolve`,
                'POST',
                null,
            );
            setResolveMessage(getPullRequestRepairSuccessMessage(result, detail.prId));
            if (result.action !== 2) {
                onResolutionStateChange?.(detail.prId, 'idle');
            }
            onResolved?.();
            loadDetail();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to resolve the pull request.');
            const errorCode = err instanceof ApiError
                && typeof err.body === 'object'
                && err.body !== null
                && 'code' in err.body
                && typeof err.body.code === 'string'
                    ? err.body.code
                    : null;
            if (errorCode === 'PullRequestRepairNotActionable') {
                if (onResolutionStateChange) {
                    onResolutionStateChange(detail.prId, 'notActionable');
                } else {
                    setLocalNotActionable(true);
                }
            }
            if (errorCode === 'PullRequestResolutionAlreadyRunning') {
                loadDetail();
                onResolved?.();
            } else if (errorCode !== 'PullRequestRepairNotActionable') {
                onResolutionStateChange?.(detail.prId, 'idle');
            }
        } finally {
            setResolveLoading(false);
        }
    }

    const standardWorkItems = detail?.workItems.filter(item => item.workItemKind === 0) ?? [];
    const repairWorkItems = detail?.workItems.filter(item => item.workItemKind === 1) ?? [];
    const adoUrl = detail ? buildAdoPullRequestUrl(service, detail.prId) : undefined;

    return (
        <Panel
            isLightDismiss
            isOpen={isOpen && taskId !== undefined && prId !== undefined}
            onDismiss={onDismiss}
            type={PanelType.extraLarge}
            headerText={prId === undefined ? 'Pull request detail' : `PR #${prId}`}
            allowTouchBodyScroll
        >
            {loading && <Spinner size={SpinnerSize.large} label="Loading pull request details..." />}
            {error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
            {resolveMessage && <MessageBar messageBarType={MessageBarType.success}>{resolveMessage}</MessageBar>}
            {detail && (
                <Stack tokens={{ childrenGap: 16 }}>
                    <section>
                        <Text as="h2" variant="large">Pull request</Text>
                        <Text block>
                            <strong>
                                {getPullRequestStatusWithHealthLabel(detail.status, detail.health, true)}
                            </strong>
                        </Text>
                        <Text block>Source work item: #{detail.sourceWorkItemId}</Text>
                        <Text block>Created: {formatDate(detail.dateCreated)}</Text>
                        <Text block>Last checked: {formatDate(detail.dateLastChecked)}</Text>
                        <Text block>Last commit: {formatDate(detail.lastCommitDate)}</Text>
                        <PullRequestRepairProgress progress={detail.repairProgress} />
                        <Stack horizontal wrap tokens={{ childrenGap: 8 }} styles={{ root: { marginTop: 12 } }}>
                            <PullRequestReviewerEmailButton
                                service={service}
                                taskListId={taskListId}
                                taskId={detail.taskId}
                                prId={detail.prId}
                                scope={detail.scope}
                                attentionSummary={getPullRequestStatusWithHealthLabel(
                                    detail.status,
                                    detail.health,
                                    true,
                                )}
                                dateCreated={detail.dateCreated}
                                signals={detail.signals ?? []}
                                reviewInspectionComplete={detail.reviewInspectionComplete}
                            />
                            {!effectiveNotActionable && canResolvePullRequest(detail, detail.workItems) && (
                                <DefaultButton
                                    text={effectiveResolving ? 'Resolving...' : 'Resolve'}
                                    disabled={effectiveResolving}
                                    onClick={() => void resolvePullRequest()}
                                />
                            )}
                            {adoUrl && (
                                <DefaultButton
                                    text="Open in Azure DevOps"
                                    href={adoUrl}
                                    target="_blank"
                                />
                            )}
                        </Stack>
                    </section>
                    <PullRequestWorkItemHistory
                        title="Standard work items"
                        workItems={standardWorkItems}
                        serviceName={serviceName}
                    />
                    <PullRequestWorkItemHistory
                        title="Repair work items"
                        workItems={repairWorkItems}
                        serviceName={serviceName}
                        isRepairHistory
                    />
                </Stack>
            )}
        </Panel>
    );
}
